import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import { useEffect, useMemo, useState } from "react";
import { Formik, ErrorMessage } from "formik";
import { ArrowRightIcon } from "lucide-react";
import { InfoIcon } from "@/components/Icons/InfoIcon";
import { AlertDescription } from "@/components/ui/alert";
import { CubeIcon } from "@/components/Icons/CubeIcon";
import { CreditCardPlusIcon } from "@/components/Icons/CreditCardPlusIcon";
import { PassportIcon } from "@/components/Icons/PassportIcon";
import * as Yup from "yup";
import { REQUIRED_MESSAGE } from "@/utils/form";
import { useNotificationStore } from "@/store/notification";
import { useToast } from "@/components/ui/use-toast";
import { AnalyticsEventType } from "@/constants/analytics";
import { trackEvent } from "@/utils/analytics";
import { useUserStore } from "@/store/user";
import { TextField } from "@/components/forms/fields";
import { PAYMENT_LOADING_HTML } from "@/constants";
import { getFirstTouchAttributions } from "@/utils/analytics";
import { SectionHeader } from "@/components/Common/SectionHeader/SectionHeader";
import { ClusterPaymentOptions } from "@/components/ClustersPage/ClusterPaymentOptions/ClusterPaymentOptions";

export type PurchaseCreditsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  className?: string;
};

export const PurchaseCreditsModal: React.FC<PurchaseCreditsModalProps> = ({ isOpen, onClose }) => {
  const topUpUserCredits = useUserStore((state) => state.topUpUserCredits);
  const initialValues = useMemo(() => {
    return {
      name: "",
      reason: "",
      amount: undefined,
      currency: "usdc"
    } as unknown as PurchaseCreditsFormType;
  }, []);
  const displayError = useNotificationStore((state) => state.displayError);
  const { toast } = useToast();
  const [shouldPollBalance, setShouldPollBalance] = useState(false);
  const fetchBalances = useUserStore((state) => state.fetchBalances);
  // const [paymentLink, setPaymentLink] = useState<string>();

  useEffect(() => {
    if (!shouldPollBalance) {
      return;
    }

    const timeout = setInterval(async () => {
      await fetchBalances(true);
    }, 5000);

    return () => {
      clearInterval(timeout);
    };
  }, [shouldPollBalance, fetchBalances]);

  return (
    <Dialog open={isOpen} modal defaultOpen={isOpen}>
      <DialogContent
        className={cn(
          "custom-scrollbar max-h-[100vh] w-full gap-0 overflow-y-auto bg-gray-dark-1400 px-0 py-0 !pr-0 lg:min-w-[560px]"
        )}
        showCloseIcon={false}
      >
        <DialogHeader className="gap-2 p-5">
          <DialogTitle className="text-2xl">Buy IO Credits</DialogTitle>
          <div className="text-base text-gray-dark-1000">
            Unlock faster processing, optimized payments, and streamlined efficiency with IO
            Credits.
          </div>
        </DialogHeader>

        <Formik<PurchaseCreditsFormType>
          initialValues={initialValues}
          onSubmit={async (values) => {
            const newWindow = window.open("", "_blank");
            try {
              const { amount, currency } = values;
              const url = new URL(window.location.href);
              url.searchParams.set("credits_purchase", "success");
              url.searchParams.set("amount", String(amount));
              url.searchParams.set("currency", currency);
              const redirectUrl = url.toString();
              if (newWindow) {
                newWindow.document.body.innerHTML = PAYMENT_LOADING_HTML;
              }
              window.dataLayer.push({
                event: AnalyticsEventType.PURCHASE_INTENT,
                ecommerce: {
                  currency: currency === "usdc" ? "USD" : "USD",
                  value: amount
                },
                attribution: {
                  ...getFirstTouchAttributions()
                },
                purchaseContext: {
                  product: "io_credits",
                  pay_method: currency === "usdc" ? "crypto" : "fiat"
                }
              });
              const response = await topUpUserCredits({
                amount,
                resourceId: "newPurchase",
                paymentMethod: currency === "usdc" ? "crypto" : "fiat",
                redirectUrl
              });
              if (newWindow) {
                newWindow.location.href = response.paymentLink;
              } else {
                window.open(response.paymentLink, "_blank");
              }

              trackEvent(AnalyticsEventType.PURCHASE_CREDIT_SUBMITTED, {
                amount
              });

              toast({ title: "Payment link generated", variant: "success" });

              setShouldPollBalance(true);

              onClose();
            } catch (e) {
              if (newWindow) {
                newWindow.document.body.innerHTML = "<p>Failed to generate payment link</p>";
              }
              if (e instanceof Error) {
                displayError({
                  title: "ERROR",
                  message: e.message
                });
              }
            }
          }}
          validationSchema={PurchaseCreditsFormSchema}
          validateOnMount={true}
          validateOnChange={true}
        >
          {({ handleSubmit, touched, errors, isValid, values }) => {
            return (
              <form onSubmit={handleSubmit} className="flex flex-col">
                <div className="flex flex-col gap-5 border-y border-gray-dark-200 p-5">
                  <div className="flex flex-col gap-2">
                    <Label>
                      Amount <span className="text-blue-light-300">*</span>
                    </Label>
                    <TextField
                      name="amount"
                      placeholder="$0.00"
                      type="number"
                      className={cn(
                        "bg-gray-dark-600 text-foreground placeholder:text-gray-light-1700 focus:border-gray-dark-200 focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0",
                        errors.amount && touched.amount
                          ? "border-red-dark-300"
                          : "border-gray-light-1900"
                      )}
                    />
                    <ErrorMessage
                      name={`amount`}
                      component="div"
                      className="subtext-x-small -mt-5 text-red-light-200"
                    />
                    <div className="flex flex-row items-center gap-1">
                      <InfoIcon className="w-[18px] min-w-[18px] text-gray-dark-1500" />
                      <AlertDescription className="text-xs font-medium text-foreground">
                        1 IO Credit = 1 USD{" "}
                        <span className="text-gray-dark-2200">worth of compute or inference</span>
                      </AlertDescription>
                    </div>

                    <div className="w-full pt-2">
                      <div className="flex w-full flex-col gap-4">
                        <SectionHeader
                          title="Payment Method"
                          subtitle="Select your payment method"
                        />
                        <ClusterPaymentOptions
                          bordered
                          showPaymentFees
                          currency={values.currency as "stripe" | "usdc" | "credits" | "iocoin"}
                          totalCost={values.amount ? Number(values.amount) : undefined}
                          order={["stripe", "usdc"]}
                          disableCreditCardWhenSufficientCredits={false}
                          className="w-full"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col p-5">
                  <ul className="flex flex-col gap-4">
                    <li className="flex flex-row items-center gap-2">
                      <CubeIcon className="h-[26px] text-gray-dark-2200" />
                      <span className="text-sm text-gray-dark-2200">Can be used for io.cloud</span>
                    </li>
                    <li className="flex flex-row items-center gap-2">
                      <CreditCardPlusIcon className="h-[26px] text-gray-dark-2200" />
                      <span className="text-sm text-gray-dark-2200">
                        Enables fully automated workflows (esp. via API)
                      </span>
                    </li>
                    <li className="flex flex-row items-center gap-2">
                      <PassportIcon className="h-[26px] text-gray-dark-2200" />
                      <span className="text-sm text-gray-dark-2200">
                        No paywall friction during deployments
                      </span>
                    </li>
                  </ul>
                </div>

                <DialogFooter className="flex flex-row !justify-center gap-2 rounded-b-lg px-4 py-4 dark:border-gray-dark-200 dark:bg-black">
                  <div className="flex-1"></div>
                  <Button
                    onClick={onClose}
                    variant="outline"
                    size="sm"
                    data-testid="ConfirmModal_Button"
                  >
                    Cancel
                  </Button>

                  <Button size="sm" className="gap-2" type="submit" disabled={!isValid}>
                    <span>Click to pay</span>
                    <ArrowRightIcon className="h-4 w-4" />
                  </Button>

                  {/* {paymentLink ? <Button
                    type="button"
                    size="sm"
                    onClick={() => {
                      window.open(paymentLink, '_blank');
                      onClose?.();
                    }}
                    className="gap-2"
                  >
                    <span>Click to pay</span>
                    <ArrowRightIcon className="h-4 w-4" />
                  </Button> : <Button
                    type="submit"
                    size="sm"
                    disabled={isSubmitting || !isValid}
                    className="gap-2"
                  >
                    {isSubmitting ? (
                      <div className="mr-2">
                        <Loader className="!h-[12px] !w-[12px]" color="#fff" />
                      </div>
                    ) : null}
                    <span>Generate payment link</span>
                    <ArrowRightIcon className="h-4 w-4" />
                  </Button>} */}
                </DialogFooter>
              </form>
            );
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

type PurchaseCreditsFormType = {
  amount: number;
  currency: string;
};

const PurchaseCreditsFormSchema = Yup.object().shape({
  amount: Yup.number()
    .required(REQUIRED_MESSAGE)
    .min(10, "Amount must be between 10 and 100,000")
    .max(100000, "Amount must be between 10 and 100,000")
    .test("max-2-decimals", "Amount cannot have more than 2 decimal places", (value) => {
      if (value === undefined || value === null) return true; // let other validators handle required
      return /^\d+(\.\d{1,2})?$/.test(value.toString());
    }),
  currency: Yup.string().required(REQUIRED_MESSAGE)
});
